import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'
import OrganizationForm from './OrganizationForm'

export default function OrganizationsTab({ selectedOrganization, onSelectOrganization }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingOrg, setEditingOrg] = useState(null)
  const [deletingOrg, setDeletingOrg] = useState(null)

  const { data: organizations, loading, error, refetch } = useSupabaseQuery('OTPOrganisations', {
    eq: { IsDeleted: false },
    order: { column: 'Name', ascending: true }
  })

  const { insert, update, softDelete, loading: mutating } = useSupabaseMutation('OTPOrganisations')

  const handleCreate = async (formData) => {
    const { error } = await insert(formData)
    if (error) {
      alert(`Failed to create organization: ${error.message}`)
    } else {
      setShowCreateModal(false)
      refetch()
    }
  }

  const handleUpdate = async (formData) => {
    const { error } = await update(editingOrg.OrganisationId, formData, 'OrganisationId')
    if (!error) {
      setEditingOrg(null)
      refetch()
      if (selectedOrganization?.OrganisationId === editingOrg.OrganisationId) {
        onSelectOrganization({ ...selectedOrganization, ...formData })
      }
    }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingOrg.OrganisationId, 'OrganisationId')
    if (!error) {
      setDeletingOrg(null)
      refetch()
      if (selectedOrganization?.OrganisationId === deletingOrg.OrganisationId) {
        onSelectOrganization(null)
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">Error loading organizations</h3>
        <p className="text-red-700 dark:text-red-300">{error.message}</p>
        <button onClick={refetch} className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">OTP Organizations</h2>
          <p className="text-gray-600 dark:text-gray-400">Manage OTP service organizations (multi-tenant isolation)</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Organization</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {organizations?.map((org) => (
          <div
            key={org.OrganisationId}
            className={`
              bg-white dark:bg-gray-800 rounded-lg border-2 p-6 transition-all cursor-pointer
              ${selectedOrganization?.OrganisationId === org.OrganisationId
                ? 'border-primary-500 shadow-lg'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }
            `}
            onClick={() => onSelectOrganization(org)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{org.Name}</h3>
                {org.Description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{org.Description}</p>
                )}
              </div>
              {selectedOrganization?.OrganisationId === org.OrganisationId && (
                <span className="ml-2 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-semibold rounded">
                  Selected
                </span>
              )}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">ID: {org.OrganisationId}</span>
              <div className="flex space-x-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingOrg(org) }}
                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeletingOrg(org) }}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}

        {(!organizations || organizations.length === 0) && (
          <div className="col-span-full text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No organizations</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new organization.</p>
          </div>
        )}
      </div>

      {showCreateModal && (
        <Modal title="Create Organization" onClose={() => setShowCreateModal(false)}>
          <OrganizationForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {editingOrg && (
        <Modal title="Edit Organization" onClose={() => setEditingOrg(null)}>
          <OrganizationForm
            organization={editingOrg}
            onSubmit={handleUpdate}
            onCancel={() => setEditingOrg(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {deletingOrg && (
        <Modal title="Delete Organization" onClose={() => setDeletingOrg(null)}>
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingOrg.Name}</strong>?
              This will affect all associated accounts.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingOrg(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
